"use client";

import Image from "next/image";
import { useState, useEffect, useId, useMemo, useCallback, useRef, type ReactNode } from "react";
import { useRouter } from "next/navigation";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn, sanitizeSearchPath, buildBrowseSearchUrl } from "@/lib/utils";
import { ga4Search } from "@/lib/analytics/ga4-ecommerce";

import SearchIcon from "@/components/icons/SearchIcon";
import ButterfliesImage from "@/../public/assets/home/hero/butterflies-hero.png";
import HeroImage from "@/../public/assets/home/hero/home-hero.png";
import LeavesColorImage from "@/../public/assets/home/hero/leaves-color.png";
import LeavesGreyImage from "@/../public/assets/home/hero/leaves-grey.png";
import DiagonalsImage from "@/../public/assets/home/hero/diagonals.png";
import TechxeedHero from "@/../public/assets/home/hero/techxeed-hero.png";
import TechxeedHeroShadow from "@/../public/assets/home/hero/tx-shadow.png";

const popularSearch = ["Website", "Mobile App", "Web Apps", "AI Apps"];

type SlideKey = "first" | "second";
type SegmentStyle = "normal" | "underline" | "underline-black" | "teal";

const TECHXEED_GREEN = "var(--color-core-secondary-aqua)";
const TECHXEED_TEAL = "var(--color-brand-primary)";
const PENCIL_COLOR = "rgba(243, 130, 108, 0.7)";
const SECOND_SLIDE_PENCIL = "#07a8bc";

const slideSegments: Record<SlideKey, Array<{ text: string; style: SegmentStyle }>> = {
	first: [
		{ text: "Find powerful ", style: "normal" },
		{ text: "AI & Software", style: "teal" },
		{ text: " solutions for ", style: "normal" },
		{ text: "service", style: "underline-black" },
		{ text: " businesses", style: "normal" },
	],
	second: [
		{ text: "Automate", style: "underline-black" },
		{ text: " and ", style: "normal" },
		{ text: "scale", style: "underline-black" },
		{ text: " your operations with ", style: "normal" },
		{ text: "AI-powered", style: "teal" },
		{ text: " ", style: "normal" },
		{ text: "systems", style: "normal" },
	],
};

const baseSentences: Record<SlideKey, string> = {
	first: slideSegments.first.map((segment) => segment.text).join(""),
	second: slideSegments.second.map((segment) => segment.text).join(""),
};

export default function Hero() {
const Highlight = ({
	children,
	color = PENCIL_COLOR,
	showLine = true,
	delaySeconds = 0,
}: {
	children: ReactNode;
	color?: string;
	showLine?: boolean;
	delaySeconds?: number;
}) => {
		// Convert color to transparent version for gradient fade
		const getTransparentColor = (col: string): string => {
			if (col.startsWith("rgba")) {
				// Extract RGB values and set alpha to 0
				const match = col.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
				if (match) {
					return `rgba(${match[1]}, ${match[2]}, ${match[3]}, 0)`;
				}
			}
			// For hex or other formats, try to append 00 for transparency
			return `${col}00`;
		};
		
		return (
			<>
				{/* SECURITY: CSS animations are static and safe - no user input */}
				<style dangerouslySetInnerHTML={{
					__html: `
						@keyframes drawPencilLine {
							from {
								transform: scaleX(0);
								opacity: 0;
							}
							to {
								transform: scaleX(1);
								opacity: 0.7;
							}
						}
					`
				}} />
				<span className="relative inline-block pb-0 align-baseline">
					<span className="relative z-10">{children}</span>
				{showLine && (
						<span
							aria-hidden
							className="pointer-events-none absolute bottom-0 left-0 h-[5px] rounded-full"
							style={{
								background: `linear-gradient(to right, ${color} 0%, ${color} 70%, ${getTransparentColor(color)} 100%)`,
								filter: "blur(0.4px)",
								width: "100%",
								opacity: 0,
								transform: "scaleX(0)",
								transformOrigin: "left",
								animation: "drawPencilLine 0.5s ease-out forwards",
								animationDelay: `${delaySeconds}s`,
								animationFillMode: "both",
							}}
						/>
					)}
				</span>
			</>
		);
	};

	const router = useRouter();
	const [searchString, setSearchString] = useState("");
	const [currentSlide, setCurrentSlide] = useState(0);
	const [hasMounted, setHasMounted] = useState(false);
	const heroSearchInputId = useId();
	const [typewriterText, setTypewriterText] = useState<Record<SlideKey, string>>({
		first: "",
		second: "",
	});
	const handleSearch = useCallback((searchString: string) => {
		const safe = sanitizeSearchPath(searchString);
		if (safe) ga4Search(safe);
		router.push(safe ? buildBrowseSearchUrl(safe) : "/");
	}, [router]);

	const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchString(e.target.value);
	}, []);

	const handlePopularSearchClick = useCallback((search: string) => {
		setSearchString(search);
		handleSearch(search);
	}, [handleSearch]);

	// Auto-cycle slides every 5 seconds
	useEffect(() => {
		const interval = setInterval(() => {
			setCurrentSlide((prev) => (prev === 0 ? 1 : 0));
		}, 10000);

		return () => clearInterval(interval);
	}, []);

	useEffect(() => {
		setHasMounted(true);
	}, []);

	useEffect(() => {
		if (!hasMounted) return;

		const slideKey: SlideKey = currentSlide === 0 ? "first" : "second";
		const targetSentence = baseSentences[slideKey];
		const timeoutRef = { current: null as NodeJS.Timeout | null };
		let isActive = true;
		let currentLength = 0;

		const updateText = () => {
			if (!isActive) return;
			
			if (currentLength >= targetSentence.length) {
				return; // Already complete
			}

			currentLength += 1;
			setTypewriterText((prev) => ({
				...prev,
				[slideKey]: targetSentence.slice(0, currentLength),
			}));

			// Schedule next update outside the setter to maintain proper timing
			if (currentLength < targetSentence.length) {
				timeoutRef.current = setTimeout(updateText, 50);
			}
		};

		// Reset and start the typewriter effect
		currentLength = 0;
		timeoutRef.current = setTimeout(updateText, 50);

		return () => {
			isActive = false;
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, [hasMounted, currentSlide]);

	useEffect(() => {
		if (!hasMounted) return;

		const inactiveSlide: SlideKey = currentSlide === 0 ? "second" : "first";
		setTypewriterText((prev) => {
			if (prev[inactiveSlide] !== "") {
				return {
					...prev,
					[inactiveSlide]: "",
				};
			}
			return prev;
		});
	}, [hasMounted, currentSlide]);

	const renderSegment = useCallback((
		slide: SlideKey,
		style: SegmentStyle,
		content: string,
		key: number,
		isComplete: boolean,
		delaySeconds: number = 0,
		slideComplete = false,
	) => {
		if (content.length === 0) return null;

		const isUnderlineStyle = style === "underline" || style === "underline-black";

		// Show underline only when the word AND entire sentence are complete
		if (isUnderlineStyle && (!isComplete || !slideComplete)) {
			const color =
				style === "underline"
					? slide === "first"
						? TECHXEED_GREEN
						: TECHXEED_TEAL
					: "var(--color-text-primary)";
			return (
				<span
					key={key}
					style={{
						color,
					}}
				>
					{content}
				</span>
			);
		}

		switch (style) {
			case "underline":
				return (
					<Highlight key={key} color={PENCIL_COLOR} showLine={isComplete} delaySeconds={delaySeconds}>
						<span
							style={{
								color: slide === "first" ? TECHXEED_GREEN : TECHXEED_TEAL,
							}}
						>
							{content}
						</span>
					</Highlight>
				);
			case "underline-black":
				return (
					<Highlight
						key={key}
						color={slide === "second" ? SECOND_SLIDE_PENCIL : "rgba(63, 224, 208, 0.8)"}
						showLine={isComplete}
						delaySeconds={delaySeconds}
					>
						<span 
							style={{
								color: "var(--color-text-primary)",
							}}
						>
							{content}
						</span>
					</Highlight>
				);
			case "teal":
				const targetColor = slide === "first" ? TECHXEED_GREEN : TECHXEED_TEAL;
				const flickerAnimation = slide === "first" ? "flicker" : "flickerTeal";
				// Start with text-primary color (black in light, white in dark), flicker only after entire sentence is complete, then stay colored
				return (
					<span
						key={key}
						style={{ 
							color: "var(--color-text-primary)",
							animation: slideComplete ? `${flickerAnimation} 2s linear forwards` : "none",
						}}
					>
						{content}
					</span>
				);
			default:
				return (
					<span key={key}>{content}</span>
				);
		}
	}, []);

	const renderSegments = useCallback((slide: SlideKey, length: number, slideComplete: boolean) => {
		const segments = slideSegments[slide];
		let remaining = length;
		const nodes: ReactNode[] = [];
		let cumulativeLength = 0;
		let hasPreviousUnderline = false; // Track if we've seen a previous underline segment
		for (let index = 0; index < segments.length; index += 1) {
			if (remaining <= 0) break;
			const segment = segments[index];
			const portion = segment.text.slice(0, remaining);
			if (portion.length > 0) {
				// Check if this specific segment is fully typed
				const segmentEnd = cumulativeLength + segment.text.length;
				const isSegmentComplete = portion.length === segment.text.length && length >= segmentEnd;
				
				// Calculate delay for underline segments: wait for previous underline to finish
				let delaySeconds = 0;
				const isUnderline = segment.style === "underline" || segment.style === "underline-black";
				if (isUnderline && isSegmentComplete) {
					if (hasPreviousUnderline) {
						// Wait for previous underline animation to finish (0.5s) plus a small gap
						delaySeconds = 0.5 + 0.1;
					}
					hasPreviousUnderline = true;
				}
				
				nodes.push(
					renderSegment(slide, segment.style, portion, index, isSegmentComplete, delaySeconds, slideComplete),
				);
			}
			remaining -= portion.length;
			cumulativeLength += segment.text.length;
		}
		return nodes;
	}, [renderSegment]);

	const renderTypewriterSegment = useCallback((slide: SlideKey) => {
		if (!hasMounted || typeof window === "undefined") {
			return <>{renderSegments(slide, baseSentences[slide].length, true)}</>;
		}

		const text = typewriterText[slide];
		const base = baseSentences[slide];
		const caretNeeded = text.length < base.length;
		const slideComplete = text.length === base.length;

		return (
			<>
				{renderSegments(slide, text.length, slideComplete)}
				{caretNeeded && (
					<span className="ml-1 inline-block h-5 w-[2px] animate-pulse bg-primary align-middle" />
				)}
			</>
		);
	}, [hasMounted, typewriterText, renderSegments]);

	const heroCopy = useMemo(() => currentSlide === 0
		? {
				title: (
					<span className="block h-28 sm:h-[7.5rem] md:h-[8.5rem]">
						{renderTypewriterSegment("first")}
					</span>
				),
				description: (
					<span className="block h-14 sm:h-16">
						With our innovative <span style={{ color: "#3FE0D0" }}>AI</span> and <span style={{ color: "#3FE0D0" }}>Software</span> solutions, you can systemise, automate and scale your business from anywhere.
					</span>
				),
		  }
		: {
				title: (
					<span className="block h-28 sm:h-[7.5rem] md:h-[8.5rem]">
						{renderTypewriterSegment("second")}
					</span>
				),
				description: (
					<span className="block h-14 sm:h-16">
						Leverage modern <span style={{ color: "#07A8BC" }}>AI</span> and <span style={{ color: "#07A8BC" }}>Software</span> to automate client onboarding, delivery, and operations for your business to run smoother and grow faster.
					</span>
				),
		  }, [currentSlide, renderTypewriterSegment]);

	return (
		<>
			{/* SECURITY: CSS animations are static and safe - no user input */}
			<style dangerouslySetInnerHTML={{
				__html: `
					@keyframes fadeInUp {
						from {
							opacity: 0;
							transform: translateY(10px);
						}
						to {
							opacity: 1;
							transform: translateY(0);
						}
					}
					@keyframes flicker {
						0% { color: var(--color-text-primary); }
						20% { color: var(--color-text-primary); }
						25% { color: var(--color-core-secondary-aqua); }
						30% { color: var(--color-text-primary); }
						35% { color: var(--color-core-secondary-aqua); }
						40% { color: var(--color-text-primary); }
						45% { color: var(--color-core-secondary-aqua); }
						50% { color: var(--color-text-primary); }
						51% { color: var(--color-core-secondary-aqua); }
						100% { color: var(--color-core-secondary-aqua); }
					}
					@keyframes flickerTeal {
						0% { color: var(--color-text-primary); }
						20% { color: var(--color-text-primary); }
						25% { color: var(--color-brand-primary); }
						30% { color: var(--color-text-primary); }
						35% { color: var(--color-brand-primary); }
						40% { color: var(--color-text-primary); }
						45% { color: var(--color-brand-primary); }
						50% { color: var(--color-text-primary); }
						51% { color: var(--color-brand-primary); }
						100% { color: var(--color-brand-primary); }
					}
				`
			}} />
			<div className="hero-gradient-background relative mt-[-90px] min-h-screen pt-16 xs:pt-20 sm:px-8 sm:pt-10 md:px-12 md:pt-14">
			<div className="container relative z-10 grid min-h-[calc(100vh-90px)] overflow-x-hidden px-4 sm:px-6 xl:grid-cols-[1fr_26rem]">
				<div className="relative z-10 flex min-w-0 flex-col justify-center text-3xl sm:max-w-[40ch]">
					{/* Slide 1: Butterflies - positioned at same level as Get Started button */}
					<div className="relative mb-4 sm:mb-2">
						<Image
							draggable="false"
							src={ButterfliesImage}
							alt="Decorative butterflies"
							aria-hidden
							sizes="(max-width: 640px) 64px, 128px"
							quality={100}
							loading="lazy"
							className={`absolute -left-4 -top-10 size-16 transition-opacity duration-1000 xs:-left-2 xs:-top-12 xs:size-20 sm:-left-4 sm:-top-14 sm:size-24 ${
								currentSlide === 0 ? "opacity-100" : "pointer-events-none opacity-0"
							}`}
						/>
						<span
							className={`inline-flex h-6 w-fit items-center rounded-full bg-[#E4FBFD] px-3 py-0.5 text-[10px] font-medium text-black transition-opacity duration-500 dark:!bg-core-midnight dark:text-foreground sm:h-8 sm:px-4 sm:py-1 sm:text-xs ${
								currentSlide === 1
									? "opacity-100"
									: "pointer-events-none opacity-0"
							}`}
						>
							Get Started
						</span>
					</div>

					<h1 className="m-0 text-2xl font-semibold !leading-snug xs:-mt-2 xs:text-2.5xl sm:mt-0 sm:text-4xl">
						{heroCopy.title}
					</h1>
					<span 
						key={currentSlide}
						className="m-0 text-xs font-light leading-tight transition-all duration-700 ease-in-out xs:text-sm sm:mt-6 sm:text-sm3 md:mt-2 lg:mt-3"
						style={{
							animation: 'fadeInUp 0.7s ease-in-out',
						}}
					>
						{heroCopy.description}
					</span>

					<div className="mt-6 flex h-14 w-full items-center gap-2 rounded-md bg-primary px-4 dark:bg-core-midnight sm:mt-3 sm:h-16">
						<SearchIcon
							className="w-6 h-6 shrink-0 text-primary-foreground dark:text-secondary"
							aria-hidden
						/>
						<label htmlFor={heroSearchInputId} className="sr-only">
							Search for services
						</label>
						<Input
							id={heroSearchInputId}
							name="heroSearch"
							type="text"
							inputMode="search"
							enterKeyHint="search"
							placeholder="What are you looking for?"
							value={searchString}
							onChange={handleInputChange}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
									handleSearch(searchString);
								}
							}}
							autoComplete="off"
							className="flex-1 rounded-none border-0 bg-transparent px-0 text-xs text-primary-foreground shadow-none outline-none ring-0 placeholder:font-light placeholder:text-grey-foreground focus:border-0 focus:outline-none focus:ring-0 focus-visible:border-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 dark:text-foreground dark:placeholder:text-muted-foreground xs:text-sm sm:text-sm"
						/>
						<Button
							className="hidden shrink-0 px-8 py-6 font-normal text-secondary-foreground transition-colors duration-300 sm:inline-flex"
							onClick={() => handleSearch(searchString)}
						>
							Search
						</Button>
					</div>

					<Button
						className="mt-4 h-12 w-full rounded-full text-xs text-secondary-foreground shadow-custom xs:h-14 xs:text-sm sm:hidden"
						onClick={() => handleSearch(searchString)}
					>
						Search
					</Button>

					<h1 className="mb-1.5 mt-8 text-[10px] font-light xs:text-xs sm:mb-3">Popular Searches</h1>
					<div
						className="z-10 mt-[15px] flex h-[24px] max-w-[calc(100vw-1rem)] items-center gap-1.5 overflow-x-auto overflow-y-hidden xs:gap-2 sm:h-auto sm:mt-0 sm:my-3 sm:overflow-y-auto md:my-2 md:gap-1.5"
						style={{ scrollbarWidth: "none" }}
					>
						{popularSearch.map((search) => (
							<button
								key={search}
								aria-label={`Search for ${search}`}
								className="flex h-[24px] min-w-[24px] shrink-0 items-center justify-center text-nowrap rounded-full bg-secondary/5 px-3 text-[10px] text-secondary transition-colors duration-300 hover:bg-secondary/20 xs:px-3.5 xs:text-[11px] sm:px-6 sm:text-xsm2 sm:h-[28px] sm:min-w-[28px] md:px-4 md:text-xs"
								onClick={() => handlePopularSearchClick(search)}
							>
								{search}
							</button>
						))}
					</div>
				</div>

				{/* Hero Images Container - Same position for both slides */}
				<div className="relative hidden w-full self-center overflow-visible xl:block xl:pb-16">
					{/* Slide 1: Original Techxeed Hero Image (SVG) */}
					<Image
						src={HeroImage}
						alt="Techxeed business automation platform showcase"
						priority
						sizes="(max-width: 1280px) 0vw, 75vw"
						quality={100}
						className={`w-full transition-opacity duration-1000 ${
							currentSlide === 0 ? "relative opacity-100" : "absolute inset-0 opacity-0"
						}`}
					/>
					{/* Slide 2: Techxeed Hero Image */}
					<div
						className={`transition-opacity duration-1000 ${
							currentSlide === 1 ? "relative opacity-100" : "absolute inset-0 opacity-0"
						}`}
					>
						<div
							className={`relative w-full transition-transform duration-1000 ${
								currentSlide === 1 ? "scale-[1.32]" : "scale-100"
							}`}
						>
							<Image
								src={TechxeedHeroShadow}
								alt=""
								aria-hidden
								fill
								sizes="(max-width: 1280px) 0vw, 75vw"
								quality={100}
								className="pointer-events-none translate-x-[-10%] scale-[0.77] select-none object-contain"
							/>
					<Image
								src={TechxeedHero}
						alt="Techxeed platform showcase"
						sizes="(max-width: 1280px) 0vw, 75vw"
						quality={100}
								className="relative w-full"
					/>
						</div>
					</div>
				</div>

				{/* Slide 1: Original Decorative Elements */}
				<Image
					draggable="false"
					src={DiagonalsImage}
					alt="Decorative diagonal pattern"
					aria-hidden
					sizes="176px"
					quality={100}
					loading="lazy"
					className={`absolute right-4 top-[8%] hidden size-44 transition-opacity duration-1000 sm:right-8 sm:top-[10%] sm:block ${
						currentSlide === 0 ? "opacity-100" : "opacity-0"
					}`}
				/>

				<Image
					src={LeavesGreyImage}
					alt="Decorative grey leaves"
					draggable="false"
					aria-hidden
					sizes="(max-width: 640px) 80px, 128px"
					quality={100}
					loading="lazy"
					className={`absolute bottom-[4%] left-0 size-20 transition-opacity duration-1000 xs:size-24 sm:-left-4 sm:bottom-[6%] sm:size-32 ${
						currentSlide === 0 ? "opacity-100" : "opacity-0"
					}`}
				/>

				<Image
					draggable="false"
					src={LeavesColorImage}
					alt="Decorative colored leaves"
					aria-hidden
					sizes="(max-width: 640px) 64px, 96px"
					quality={100}
					loading="lazy"
					className={`absolute bottom-[4%] right-4 size-16 transition-opacity duration-1000 xs:right-6 xs:size-20 sm:bottom-[6%] sm:right-8 sm:size-24 ${
						currentSlide === 0 ? "opacity-100" : "opacity-0"
					}`}
				/>
			</div>
		</div>
		</>
	);
}
